// src/components/CheckiePage.js
import React, { useState } from 'react';
import { Receipt, Users, Smartphone, QrCode, Download, Zap, Globe, Shield, Instagram, ChevronDown, CheckCircle, ArrowRight, Timer } from 'lucide-react';
import QRCode from 'react-qr-code';

// ─── Fade In ─────────────────────────────────────────────────────────────────
const FadeInSection = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef();
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.07 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref}
      className={`transition-all duration-[1100ms] ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: isVisible ? `${delay}ms` : '0ms' }}>
      {children}
    </div>
  );
};

// ─── QR Code Toggle ───────────────────────────────────────────────────────────
const QRCodeComponent = ({ iosUrl }) => {
  const [selectedStore, setSelectedStore] = useState('ios');
  return (
    <div>
      <div className="mb-5 flex rounded-full p-1 border"
        style={{ background: 'rgba(64,0,228,0.06)', borderColor: 'rgba(64,0,228,0.15)' }}>
        <button onClick={() => setSelectedStore('ios')}
          className={`flex-1 px-4 py-2 rounded-full text-xs font-medium tracking-wider uppercase transition-all duration-300`}
          style={{
            background: selectedStore === 'ios' ? '#4000E4' : 'transparent',
            color: selectedStore === 'ios' ? '#fff' : '#4000E4'
          }}>
          iOS
        </button>
        <button onClick={() => setSelectedStore('android')}
          className={`flex-1 px-4 py-2 rounded-full text-xs font-medium tracking-wider uppercase transition-all duration-300`}
          style={{
            background: selectedStore === 'android' ? '#4000E4' : 'transparent',
            color: selectedStore === 'android' ? '#fff' : '#4000E4'
          }}>
          Android
        </button>
      </div>
      <div className="bg-white p-4 rounded-2xl shadow-2xl">
        {selectedStore === 'ios' ? (
          <QRCode value={iosUrl} size={148} style={{ height: 'auto', maxWidth: '100%', width: '100%' }} viewBox="0 0 256 256" />
        ) : (
          <div className="w-36 h-36 bg-gray-100 rounded-xl flex flex-col items-center justify-center gap-2">
            <QrCode className="w-10 h-10 text-gray-300" />
            <p className="text-xs text-gray-400 font-medium">Coming Soon</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const CheckiePage = ({ setCurrentPage }) => {
  const appStoreUrl = "https://apps.apple.com/us/app/checkie-split-happens/id6747963538";
  const [activeTab, setActiveTab] = useState('restaurant');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }}>

      {/* ── Fonts & Global Styles ── */}
      <style>{`
        .cki { font-family: 'Outfit', sans-serif; }
        .cki-serif { font-family: 'Cormorant Garamond', serif !important; }

        .rule { height: 1px; background: linear-gradient(to right, transparent, rgba(0,0,0,0.1), transparent); }
        .rule-left { height: 1px; background: linear-gradient(to right, rgba(0,0,0,0.12), transparent); }

        .gold-line {
          width: 40px; height: 2px;
          background: linear-gradient(to right, #4000E4, #7B4FFF);
          display: inline-block; flex-shrink: 0;
        }

        .stat-card {
          border: 1px solid rgba(0,0,0,0.07);
          background: #ffffff;
          transition: all 0.4s ease;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }
        .stat-card:hover {
          border-color: rgba(64,0,228,0.4);
          box-shadow: 0 4px 16px rgba(64,0,228,0.12);
        }

        .tab-pill {
          transition: all 0.3s ease;
          border: 1px solid rgba(0,0,0,0.12);
          font-family: 'Outfit', sans-serif !important;
          font-size: 0.7rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          font-weight: 500;
          cursor: pointer;
        }
        .tab-pill.active {
          background: #4000E4;
          border-color: #4000E4;
          color: #ffffff;
        }
        .tab-pill.inactive {
          color: rgba(0,0,0,0.45);
        }
        .tab-pill.inactive:hover {
          border-color: rgba(0,0,0,0.3);
          color: rgba(0,0,0,0.7);
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-text-1 { animation: fadeUp 1s ease-out 0.2s both; }
        .hero-text-2 { animation: fadeUp 1s ease-out 0.5s both; }
        .hero-text-3 { animation: fadeUp 1s ease-out 0.8s both; }
        .hero-cta    { animation: fadeUp 1s ease-out 1.1s both; }

        .snippet-frame {
          position: absolute; inset: 28px 0 0 0;
          opacity: 0; pointer-events: none;
        }
        .snippet-frame:first-of-type { position: relative; inset: auto; visibility: hidden; }
        @keyframes snippetCycle {
          0%   { opacity: 0; }
          5%   { opacity: 1; }
          30%  { opacity: 1; }
          38%  { opacity: 0; }
          100% { opacity: 0; }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.2; }
          5%, 30%  { opacity: 1; }
          38%      { opacity: 0.2; }
        }
      `}</style>

      <div className="cki relative">

        {/* ══ NAV ══════════════════════════════════════════════════════════════ */}
        <nav className="fixed top-0 left-0 right-0 z-50 px-6 sm:px-10 py-5 flex items-center justify-between"
          style={{ background: 'linear-gradient(to bottom, rgba(247,244,239,0.98) 70%, transparent)', backdropFilter: 'blur(8px)' }}>
          <button onClick={() => setCurrentPage('home')}
            className="cki-serif text-xl sm:text-2xl font-semibold tracking-[0.15em] uppercase"
            style={{ color: '#1a1a1a', letterSpacing: '0.18em' }}>
            Checks Intel
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => setActiveTab('restaurant')}
              className={`tab-pill px-4 py-2 rounded-full ${activeTab === 'restaurant' ? 'active' : 'inactive'}`}>
              Restaurants
            </button>
            <button onClick={() => setActiveTab('consumer')}
              className={`tab-pill px-4 py-2 rounded-full ${activeTab === 'consumer' ? 'active' : 'inactive'}`}>
              For You & Friends
            </button>
          </div>
        </nav>

        {/* ══ HERO ═════════════════════════════════════════════════════════════ */}
        <div className="relative min-h-[92vh] flex items-end" style={{ background: '#F7F4EF' }}>

          {/* Subtle purple glow */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 60% at 60% 40%, rgba(64,0,228,0.07) 0%, transparent 70%)' }} />

          {/* Decorative large number */}
          <div className="absolute right-8 sm:right-16 top-1/2 -translate-y-1/2 cki-serif select-none pointer-events-none"
            style={{ fontSize: 'clamp(160px, 25vw, 320px)', color: 'rgba(64,0,228,0.06)', lineHeight: 1, fontWeight: 600 }}>
            {activeTab === 'restaurant' ? '01' : '✓'}
          </div>

          {/* Copy */}
          <div className="relative z-10 w-full px-6 sm:px-12 lg:px-20 pb-20 sm:pb-28 pt-36">
            <div className="max-w-4xl">
              <div className="hero-text-1 flex items-center gap-4 mb-6">
                <span className="gold-line" />
                <span className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#4000E4' }}>
                  {activeTab === 'restaurant' ? 'For Restaurants' : 'Split with Friends'}
                </span>
              </div>
              {activeTab === 'restaurant' ? (
                <>
                  <h1 className="hero-text-2 cki-serif leading-[0.92] mb-8"
                    style={{ fontSize: 'clamp(48px, 8vw, 110px)', color: '#1a1a1a', fontWeight: 400 }}>
                    Your tables,<br />
                    <em style={{ color: '#4000E4' }}>turning faster.</em>
                  </h1>
                  <p className="hero-text-3 text-base sm:text-lg max-w-xl leading-relaxed mb-10"
                    style={{ color: '#666', fontWeight: 300 }}>
                    Group checks are the slowest part of any shift. We fix that —
                    with a single QR code your servers put on the table.
                    Guests split and pay themselves. Zero cost to you.
                  </p>
                  <div className="hero-cta flex flex-col sm:flex-row gap-3">
                    <a href="mailto:contact@checksintel.com?subject=Restaurant Partner Inquiry"
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-semibold tracking-wider transition-all duration-300 hover:scale-105 shadow-sm"
                      style={{ background: '#4000E4', color: '#F5F0E8', letterSpacing: '0.08em' }}>
                      Get Your Free QR Code <ArrowRight className="w-4 h-4" />
                    </a>
                    <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-medium tracking-wider border transition-all duration-300 hover:bg-black/5"
                      style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#555', letterSpacing: '0.08em' }}>
                      See How It Works <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="hero-text-2 cki-serif leading-[0.92] mb-8"
                    style={{ fontSize: 'clamp(48px, 8vw, 110px)', color: '#1a1a1a', fontWeight: 400 }}>
                    No math.<br />
                    <em style={{ color: '#4000E4' }}>No awkwardness.</em>
                  </h1>
                  <p className="hero-text-3 text-base sm:text-lg max-w-xl leading-relaxed mb-10"
                    style={{ color: '#666', fontWeight: 300 }}>
                    Scan any receipt, split it however you want, and send
                    everyone a payment link in seconds. The easiest way to
                    settle up after dinner.
                  </p>
                  <div className="hero-cta flex flex-col sm:flex-row gap-3">
                    <a href={appStoreUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-semibold tracking-wider transition-all duration-300 hover:scale-105 shadow-sm"
                      style={{ background: '#4000E4', color: '#F5F0E8', letterSpacing: '0.08em' }}>
                      <Download className="w-4 h-4" /> Download for iOS <ArrowRight className="w-4 h-4" />
                    </a>
                    <a href="https://checkie.app" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-medium tracking-wider border transition-all duration-300 hover:bg-black/5"
                      style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#555', letterSpacing: '0.08em' }}>
                      <Globe className="w-4 h-4" /> Try the Web App
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ══ RESTAURANT TAB ═══════════════════════════════════════════════════ */}
        {activeTab === 'restaurant' && (
          <>
            {/* Problem */}
            <div id="how-it-works" className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32">
              <FadeInSection>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="gold-line" />
                      <span className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#4000E4' }}>The Real Cost</span>
                    </div>
                    <h2 className="cki-serif mb-6 leading-tight"
                      style={{ fontSize: 'clamp(36px, 5vw, 64px)', color: '#1a1a1a', fontWeight: 400 }}>
                      Every group check<br />costs you a table.
                    </h2>
                    <p className="font-light leading-relaxed mb-8" style={{ color: '#555', fontSize: '1.05rem' }}>
                      When a party of six asks to split, your server stops everything —
                      running cards, doing math, coming back three times. That table
                      sits occupied for 10–15 extra minutes per group. During a Friday
                      dinner rush, that's real revenue walking out the door.
                    </p>
                    <div className="rule-left mb-8" />
                    <p className="font-light text-sm leading-relaxed" style={{ color: '#888' }}>
                      We built Checkie to solve this — not with a new POS, not with an
                      iPad at the table, but with something every diner already has in
                      their pocket.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { num: '8 min', label: 'Average time lost per group check', note: 'per table, per visit' },
                      { num: '3×', label: 'More server trips for split checks', note: 'vs. single payment' },
                      { num: '$0', label: 'Cost to your restaurant', note: 'free forever' },
                      { num: '<2 min', label: 'With Checkie QR at the table', note: 'guests handle it themselves' },
                    ].map(({ num, label, note }) => (
                      <div key={label} className="stat-card rounded-2xl p-6">
                        <div className="cki-serif mb-2" style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: '#4000E4', fontWeight: 500 }}>{num}</div>
                        <div className="text-sm font-medium mb-1" style={{ color: '#1a1a1a' }}>{label}</div>
                        <div className="text-xs" style={{ color: '#999' }}>{note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeInSection>
            </div>

            <div className="rule mx-6 sm:mx-20" />

            {/* How it works */}
            <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32">
              <FadeInSection>
                <div className="text-center mb-16">
                  <div className="flex items-center justify-center gap-4 mb-5">
                    <span className="gold-line" />
                    <span className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#4000E4' }}>Process</span>
                    <span className="gold-line" />
                  </div>
                  <h2 className="cki-serif" style={{ fontSize: 'clamp(32px, 5vw, 56px)', color: '#1a1a1a', fontWeight: 400 }}>
                    Three steps. Zero friction.
                  </h2>
                </div>
              </FadeInSection>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { n: '01', icon: QrCode, title: 'Place the QR code', body: "We email you a print-ready card. Put it on every table. That's the entire setup — no POS integration, no software, no training." },
                  { n: '02', icon: Smartphone, title: 'Guests scan & split', body: "Diners open their phone, see the itemized bill, assign what's theirs, and pay friends directly through Venmo, Cash App, or Zelle." },
                  { n: '03', icon: Timer, title: 'Table clears faster', body: 'Your server drops the check once and moves on. The group handles the rest. The table is yours again in minutes, not fifteen.' },
                ].map(({ n, icon: Icon, title, body }, i) => (
                  <FadeInSection key={n} delay={i * 120}>
                    <div>
                      <div className="text-xs tracking-[0.3em] mb-5 font-semibold" style={{ color: '#4000E4' }}>{n}</div>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ border: '1px solid rgba(64,0,228,0.3)', background: 'rgba(64,0,228,0.08)' }}>
                        <Icon className="w-5 h-5" style={{ color: '#4000E4' }} />
                      </div>
                      <h3 className="cki-serif text-xl mb-3" style={{ color: '#1a1a1a', fontWeight: 500 }}>{title}</h3>
                      <p className="text-sm font-light leading-relaxed" style={{ color: '#666' }}>{body}</p>
                    </div>
                  </FadeInSection>
                ))}
              </div>
            </div>

            <div className="rule mx-6 sm:mx-20" />

            {/* Server angle + comparison */}
            <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                <FadeInSection>
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="gold-line" />
                      <span className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#4000E4' }}>For Your Staff</span>
                    </div>
                    <h2 className="cki-serif mb-6 leading-tight"
                      style={{ fontSize: 'clamp(30px, 4vw, 50px)', color: '#1a1a1a', fontWeight: 400 }}>
                      Your servers<br />will ask for this.
                    </h2>
                    <p className="font-light leading-relaxed mb-6" style={{ color: '#555', fontSize: '1.05rem' }}>
                      Ask any server what they dread most on a busy night — it's the
                      six-top asking to split six ways. Checkie removes that entirely.
                      They drop the check. They walk away. The table tips better because
                      the server was present, not stuck at the POS running math.
                    </p>
                    <div className="space-y-3">
                      {[
                        'No more "can you split this 5 ways?"',
                        'Servers close group tables in one trip',
                        'More time on the floor, less time at the terminal',
                        'Happier staff, better service, better tips',
                      ].map(pt => (
                        <div key={pt} className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 shrink-0" style={{ color: '#4000E4' }} />
                          <span className="text-sm font-light" style={{ color: '#555' }}>{pt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </FadeInSection>

                <FadeInSection delay={200}>
                  <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.08)', background: '#fff' }}>
                    <div className="grid grid-cols-3 px-5 py-3"
                      style={{ background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <span className="text-xs tracking-wider uppercase font-medium" style={{ color: '#aaa' }}>Scenario</span>
                      <span className="text-xs tracking-wider uppercase font-medium text-center" style={{ color: '#aaa' }}>Without</span>
                      <span className="text-xs tracking-wider uppercase font-medium text-center" style={{ color: '#4000E4' }}>With Checkie</span>
                    </div>
                    {[
                      { label: 'Group of 6 pays', without: '10–15 min', with: '2–3 min' },
                      { label: 'Server trips per check', without: '3–4 trips', with: '1 trip' },
                      { label: 'Setup cost', without: '—', with: '$0' },
                      { label: 'Tech integration', without: '—', with: 'None needed' },
                      { label: 'Staff training', without: '—', with: 'None needed' },
                    ].map(({ label, without, with: w }, i) => (
                      <div key={label} className="grid grid-cols-3 px-5 py-4"
                        style={{ borderBottom: i < 4 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                        <span className="text-xs font-light" style={{ color: '#666' }}>{label}</span>
                        <span className="text-xs text-center font-light" style={{ color: '#bbb' }}>{without}</span>
                        <span className="text-xs text-center font-semibold" style={{ color: '#4000E4' }}>{w}</span>
                      </div>
                    ))}
                  </div>
                </FadeInSection>
              </div>
            </div>

            <div className="rule mx-6 sm:mx-20" />

            {/* Meet Checkie */}
            <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32">
              <FadeInSection>
                <div className="text-center mb-16">
                  <div className="flex items-center justify-center gap-4 mb-5">
                    <span className="gold-line" />
                    <span className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#4000E4' }}>The Product</span>
                    <span className="gold-line" />
                  </div>
                  <h2 className="cki-serif mb-4" style={{ fontSize: 'clamp(32px, 5vw, 60px)', color: '#1a1a1a', fontWeight: 400 }}>
                    Meet <em style={{ color: '#4000E4' }}>checkie</em>
                  </h2>
                  <p className="font-light max-w-xl mx-auto text-base" style={{ color: '#777' }}>
                    The app your guests use to scan, split, and settle. Available on iOS and the web — nothing to install for your restaurant.
                  </p>
                </div>
              </FadeInSection>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: Receipt, title: 'AI Receipt Scanning', body: 'Guests photograph the receipt. Checkie itemizes it automatically — every line, every modifier, tax and tip included.', color: '#22c55e' },
                  { icon: Users, title: 'Assign & Split', body: 'Each person selects their items. Even-splits and custom splits both work. Everyone sees exactly what they owe.', color: '#3b82f6' },
                  { icon: Zap, title: 'Pay & Settle', body: 'Deep links to Venmo, Cash App, and Zelle. Money moves peer-to-peer. Your restaurant is completely out of it.', color: '#4000E4' },
                ].map(({ icon: Icon, title, body, color }) => (
                  <FadeInSection key={title} delay={100}>
                    <div className="stat-card rounded-2xl p-7">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                        style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <h3 className="cki-serif text-xl mb-3" style={{ color: '#1a1a1a', fontWeight: 500 }}>{title}</h3>
                      <p className="text-sm font-light leading-relaxed" style={{ color: '#666' }}>{body}</p>
                    </div>
                  </FadeInSection>
                ))}
              </div>
            </div>

            <div className="rule mx-6 sm:mx-20" />

            {/* CTA + Contact Form */}
            <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32">
              <FadeInSection>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

                  {/* Left — headline */}
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="gold-line" />
                      <span className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#4000E4' }}>Get Started</span>
                    </div>
                    <h2 className="cki-serif mb-5 leading-tight"
                      style={{ fontSize: 'clamp(32px, 4vw, 56px)', color: '#1a1a1a', fontWeight: 400 }}>
                      Ready to reclaim<br />your table time?
                    </h2>
                    <p className="font-light text-base leading-relaxed mb-8" style={{ color: '#666' }}>
                      We'll send you a print-ready QR code within 24 hours.
                      No contracts. No monthly fees. No integration work.
                    </p>
                    <div className="space-y-3 text-sm font-light" style={{ color: '#999' }}>
                      {['Free to join — always', 'No POS integration required', 'Works with any restaurant', 'Setup in under 5 minutes'].map(item => (
                        <div key={item} className="flex items-center gap-3">
                          <CheckCircle className="w-4 h-4 shrink-0" style={{ color: '#4000E4' }} />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right — contact form */}
                  <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm" style={{ border: '1px solid rgba(0,0,0,0.07)' }}>
                    <h3 className="cki-serif text-2xl mb-2" style={{ color: '#1a1a1a', fontWeight: 500 }}>Send us a message</h3>
                    <p className="text-sm font-light mb-7" style={{ color: '#999' }}>We typically respond within a few hours.</p>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const data = new FormData(e.target);
                        const name = data.get('name');
                        const restaurant = data.get('restaurant');
                        const email = data.get('email');
                        const message = data.get('message');
                        const subject = encodeURIComponent(`Restaurant Partner Inquiry — ${restaurant}`);
                        const body = encodeURIComponent(`Name: ${name}\nRestaurant: ${restaurant}\nEmail: ${email}\n\n${message}`);
                        window.location.href = `mailto:contact@checksintel.com?subject=${subject}&body=${body}`;
                      }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#888' }}>Your Name</label>
                          <input
                            name="name"
                            required
                            placeholder="Jane Smith"
                            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                            style={{ border: '1px solid rgba(0,0,0,0.1)', background: '#fafafa', color: '#1a1a1a' }}
                            onFocus={e => e.target.style.borderColor = '#4000E4'}
                            onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#888' }}>Restaurant Name</label>
                          <input
                            name="restaurant"
                            required
                            placeholder="The Golden Fork"
                            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                            style={{ border: '1px solid rgba(0,0,0,0.1)', background: '#fafafa', color: '#1a1a1a' }}
                            onFocus={e => e.target.style.borderColor = '#4000E4'}
                            onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#888' }}>Your Email</label>
                        <input
                          name="email"
                          type="email"
                          required
                          placeholder="jane@restaurant.com"
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                          style={{ border: '1px solid rgba(0,0,0,0.1)', background: '#fafafa', color: '#1a1a1a' }}
                          onFocus={e => e.target.style.borderColor = '#4000E4'}
                          onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#888' }}>Message <span style={{ color: '#bbb', fontWeight: 300 }}>(optional)</span></label>
                        <textarea
                          name="message"
                          rows={4}
                          placeholder="Tell us about your restaurant — number of tables, typical group sizes, anything you'd like us to know."
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 resize-none"
                          style={{ border: '1px solid rgba(0,0,0,0.1)', background: '#fafafa', color: '#1a1a1a' }}
                          onFocus={e => e.target.style.borderColor = '#4000E4'}
                          onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full py-4 rounded-full text-sm font-semibold tracking-wider transition-all duration-300 hover:scale-[1.02] hover:brightness-110 flex items-center justify-center gap-2"
                        style={{ background: '#4000E4', color: '#F5F0E8', letterSpacing: '0.08em' }}>
                        Send Message <ArrowRight className="w-4 h-4" />
                      </button>
                      <p className="text-xs text-center" style={{ color: '#bbb' }}>
                        Or email us directly at{' '}
                        <a href="mailto:contact@checksintel.com" className="hover:underline" style={{ color: '#4000E4' }}>
                          contact@checksintel.com
                        </a>
                      </p>
                    </form>
                  </div>

                </div>
              </FadeInSection>
            </div>
          </>
        )}

        {/* ══ CONSUMER TAB ═════════════════════════════════════════════════════ */}
        {activeTab === 'consumer' && (
          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-24 sm:py-32">
            <FadeInSection>
              <div className="max-w-2xl mb-20">
                <div className="flex items-center gap-4 mb-6">
                  <span className="gold-line" />
                  <span className="text-xs tracking-[0.3em] uppercase font-medium" style={{ color: '#4000E4' }}>The App</span>
                </div>
                <h2 className="cki-serif mb-5 leading-tight"
                  style={{ fontSize: 'clamp(36px, 5vw, 64px)', color: '#1a1a1a', fontWeight: 400 }}>
                  Splitting the check<br />should take <em style={{ color: '#4000E4' }}>seconds.</em>
                </h2>
                <p className="font-light text-base leading-relaxed" style={{ color: '#666' }}>
                  Checkie scans any receipt, splits it any way you want,
                  and sends everyone a payment link. No math. No awkwardness.
                  No "I'll get you next time."
                </p>
              </div>
            </FadeInSection>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
              <FadeInSection delay={100}>
                <div className="space-y-4">
                  {[
                    { icon: Receipt, color: '#22c55e', title: 'Instant Receipt Scanning', desc: 'AI reads the receipt in seconds — every item, every modifier, tax and tip handled automatically.' },
                    { icon: Users, color: '#3b82f6', title: 'Group Splitting', desc: 'Assign items to people, split evenly, or mix both. Works for 2 people or 12.' },
                    { icon: Zap, color: '#4000E4', title: 'Real-time Payments', desc: 'One tap sends a Venmo, Cash App, or Zelle request. Everyone settles before they leave the table.' },
                    { icon: Shield, color: '#ec4899', title: 'Private & Secure', desc: "Your receipts and contacts stay on your device. We don't sell your data." },
                  ].map(({ icon: Icon, color, title, desc }) => (
                    <div key={title} className="stat-card rounded-2xl p-5 flex items-start gap-4">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                        style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
                        <Icon className="w-4 h-4" style={{ color }} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold mb-1" style={{ color: '#1a1a1a' }}>{title}</h3>
                        <p className="text-sm font-light leading-relaxed" style={{ color: '#777' }}>{desc}</p>
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 space-y-3">
                    <a href={appStoreUrl} target="_blank" rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-full text-sm font-semibold tracking-wider transition-all hover:scale-[1.02]"
                      style={{ background: '#4000E4', color: '#F5F0E8', letterSpacing: '0.08em' }}>
                      <Download className="w-4 h-4" /> Download for iOS
                    </a>
                    <a href="https://checkie.app" target="_blank" rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-full text-sm font-medium tracking-wider border transition-all hover:bg-black/5"
                      style={{ borderColor: 'rgba(0,0,0,0.15)', color: '#555', letterSpacing: '0.08em' }}>
                      <Globe className="w-4 h-4" /> Open Web App <ArrowRight className="w-4 h-4 ml-auto" />
                    </a>
                  </div>
                </div>
              </FadeInSection>

              <FadeInSection delay={250}>
                <div className="flex flex-col items-center">
                  {/* QR card */}
                  <div className="rounded-2xl p-7 w-full max-w-xs"
                    style={{ background: '#F7F4EF', border: '1px solid rgba(64,0,228,0.15)', boxShadow: '0 2px 16px rgba(64,0,228,0.08)' }}>
                    <p className="text-xs tracking-[0.25em] uppercase mb-5 text-center font-medium" style={{ color: '#4000E4' }}>Scan to Download</p>
                    <QRCodeComponent iosUrl={appStoreUrl} />
                    <p className="text-xs text-center mt-4 font-light" style={{ color: '#999' }}>Available on iOS · Web App coming to Android</p>
                  </div>
                </div>
              </FadeInSection>
            </div>
          </div>
        )}

        {/* ══ FOOTER ═══════════════════════════════════════════════════════════ */}
        <div className="rule mx-6 sm:mx-20" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <span className="cki-serif text-lg font-semibold tracking-[0.15em] uppercase" style={{ color: '#bbb' }}>
              Checks Intel
            </span>
            <div className="flex items-center gap-6 text-xs font-light" style={{ color: '#aaa' }}>
              <a href="https://www.instagram.com/getcheckie/" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-gray-800 transition-colors" style={{ color: 'inherit' }}>
                <Instagram className="w-3.5 h-3.5" /> @getcheckie
              </a>
              <span style={{ color: '#ddd' }}>|</span>
              <a href="mailto:contact@checksintel.com"
                className="hover:text-gray-800 transition-colors" style={{ color: 'inherit' }}>
                contact@checksintel.com
              </a>
            </div>
            <span className="text-xs" style={{ color: '#ccc' }}>© 2025 Checks Intel Inc.</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CheckiePage;